// React imports
import React from 'react';
import { Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';

// Material-UI imports
import {
  Box,
  Button,
  Container,
  CircularProgress,
  CssBaseline,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { VisibilityOffOutlined, VisibilityOutlined } from '@material-ui/icons';
// @ts-ignore
import Alert from '@material-ui/lab/Alert';

// Absolute imports
import Logo from 'assets/Diligent_Equity.png';
import { routes } from 'routes';
import getVersion from 'utils/version';

// Relative imports
import useStyles from './LoginStyles';
import {
  useLoginPage,
  INVALID_EMAIL_ADDRESS_ERROR,
  NO_EMAIL_ADDRESS_ERROR,
} from './useLoginPage';

export function LoginPage(props: { query: string }) {
  const classes = useStyles();
  const logic = useLoginPage(props.query);

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Paper variant="outlined" square className={`${classes.paper}`} classes={{
        root: "lens-login-form"
      }}>
        <img src={Logo} alt="logo" className={classes.logo} />

        {/* Login with password + SSO + Platform - section */}
        <Box hidden={logic.isPasswordChange}>
          <form className={classes.form} onSubmit={logic.getOnSubmitLogin()}>
            <label className={`${classes.inputLabel} lens-ee-label lens-m-0`}>
              Sign in using your email
            </label>
            <Controller
              name="email"
              control={logic.formLogin.control}
              render={(props) => (
                <TextField
                  {...props}
                  error={!!logic.formLogin.errors.email}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  autoComplete="email"
                  helperText={logic.formLogin.errors.email?.message || null}
                  classes={{ root: `${classes.formControl}` }}
                  disabled={logic.loginInProgress}
                />
              )}
            />

            {/* Login with password */}
            {logic.continueToPassword && (
              <>
                <label className={`${classes.inputLabel} lens-ee-label lens-m-0 lens-mt-2`}>Password</label>
                <Controller
                  name="password"
                  control={logic.formLogin.control}
                  render={(props) => (
                    <TextField
                      {...props}
                      error={!!logic.formLogin.errors.password}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      type={logic.showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      helperText={
                        logic.formLogin.errors.password?.message || null
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={logic.toggleShowPassword}
                              edge="end"
                            >
                              {logic.showPassword ? (
                                <VisibilityOutlined />
                              ) : (
                                <VisibilityOffOutlined />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                        classes: {
                          root: "lens-text-field-root",
                        }
                      }}
                      classes={{ root: classes.formControl }}
                      disabled={logic.loginInProgress}
                    />
                  )}
                />
                {logic.errorMessage && (
                  <Alert className="mt-4" severity="error">
                    {logic.errorMessage}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={logic.loginInProgress}
                >
                  {logic.loginInProgress ? <CircularProgress /> : 'Sign in'}
                </Button>

                <Box>
                  <Typography
                    style={{ marginBottom: '28px' }}
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    <NavLink
                      to={routes.PASSWORD_RESET}
                      color="textSecondary"
                      className={classes.link}
                    >
                      Forgot password?
                    </NavLink>
                  </Typography>
                </Box>
              </>
            )}

            {/* Intermediate button for Password login */}
            {!logic.continueToPassword && !logic.isSSOLogin && (
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={logic.loginMetaLoading}
                onClick={() => {
                  if (logic.isNonPlatformLoginExists) {
                    logic.setContinueToPassword(true);
                  } else {
                    logic.formLogin.setError('email', {
                      type: 'custom',
                      message:
                        logic.formLogin.getValues('email') === ''
                          ? NO_EMAIL_ADDRESS_ERROR
                          : INVALID_EMAIL_ADDRESS_ERROR,
                    });
                  }
                }}
              >
                {logic.loginMetaLoading ? <CircularProgress /> : 'Continue'}
              </Button>
            )}

            {/* SSO login */}
            {logic.isSSOLogin && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                className={classes.sso}
                onClick={logic.loginSso}
              >
                Single Sign On
              </Button>
            )}

            <hr className="mt-3 mb-1" />

            {/* Platform login */}
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              className={classes.platformOidc}
              onClick={logic.loginPlatformOidc}
            >
              Sign in using Diligent One Platform
            </Button>
          </form>
        </Box>

        {/* Change password form */}
        {logic.isPasswordChange && (
          <form
            className={classes.form}
            onSubmit={logic.getOnSubmitPasswordChange()}
          >
            <Box pt={0} pb={2}>
              <Typography variant="h5" align="center">
                Password Expired
              </Typography>
            </Box>
            <Typography variant="body2" gutterBottom>
              Your password has expired. Please create a new password. In order
              to protect your account, make sure your password complies with the
              below requirements:
            </Typography>
            <ul className={classes.passwordRequirements}>
              <li>at least 8 characters</li>
              <li>at least 1 number</li>
              <li>at least 1 upper case letter</li>
              <li>at least 1 lower case letter</li>
            </ul>
            <label className={classes.inputLabel}>New Password</label>
            <Controller
              name="password1"
              control={logic.formPasswordChange.control}
              render={(props) => (
                <TextField
                  {...props}
                  error={!!logic.formPasswordChange.errors.password1}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  type={logic.showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  helperText={
                    logic.formPasswordChange.errors.password1?.message || ' '
                  }
                  classes={{ root: classes.formControl }}
                />
              )}
            />
            <label className={classes.inputLabel}>Re-enter New Password</label>
            <Controller
              name="password2"
              control={logic.formPasswordChange.control}
              render={(props) => (
                <TextField
                  {...props}
                  error={!!logic.formPasswordChange.errors.password2}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  type={logic.showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  helperText={
                    logic.formPasswordChange.errors.password2?.message || ' '
                  }
                  classes={{ root: classes.formControl }}
                />
              )}
            />

            {logic.errorMessage && (
              <Alert severity="error">
                {Array.isArray(logic.errorMessage) ? (
                  <ul className={classes.errorArray}>
                    {logic.errorMessage.map((msg) => (
                      <li>{msg}</li>
                    ))}
                  </ul>
                ) : (
                  logic.errorMessage
                )}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submitPasswordChange}
              disabled={logic.loginInProgress}
            >
              {logic.loginInProgress ? <CircularProgress /> : 'Change Password'}
            </Button>
          </form>
        )}

        {/* Footer */}
        <Box
          pt={1}
          width={'100%'}
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography
            variant="body2"
            color="textPrimary"
            className={classes.privacyStatement}
            onClick={() => {
              // @ts-ignore
              window.open('https://www.diligent.com/privacy/', '_blank');
            }}
          >
            Privacy statement
          </Typography>
          <Typography
            variant="body2"
            color="textPrimary"
            className={classes.version}
            onClick={() => { }}
          >
            {getVersion()}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}
